import * as _ from 'lodash';

/**
 This is creating a circular dependency between the reports-sandbox-contract and util-iframe.
 We are anyways accepting WindowMessagePayload with Embaded actions, which is generic only.
 so there is no point of using EmbedActions from reports-sandbox-contract here.
 so, commenting out the import for now.
 */
//import { EmbedActions } from '@mode/reports/sandbox/contract';

// This holds the list of subscribers for each message
interface Subscriptions {
  [s: string]: Array<Subscription>;
}

interface Subscription {
  handler: (msg: any) => any;
  context: any;
}

export interface WindowMessagePayload {
  type: MSG_TYPES;
  [index: string]: any;
}

export enum MSG_TYPES {
  GetNewSize = 'getNewSize',

  SelectTableColumn = 'selectTableColumn',

  EnterFullScreen = 'enterFullScreen',
  ExitFullScreen = 'exitFullScreen',

  UpdateFeatureState = 'updateFeatureState',
}

export class WindowMessageService {
  static MSG_TYPES = MSG_TYPES;

  private subscriptions: Subscriptions = {};

  static $inject = ['$window'];

  public constructor(private $window: Window) {
    $window.addEventListener('message', this.handleMessage);
  }

  handleMessage = (msg: any) => {
    if (msg.data.type) {
      if (this.subscriptions[msg.data.type]) {
        this.subscriptions[msg.data.type].forEach(function (sub) {
          sub.handler.call(sub.context, msg.data);
        });
      }

      if (this.subscriptions['*']) {
        this.subscriptions['*'].forEach(function (sub) {
          sub.handler.call(sub.context, msg.data);
        });
      }
    }
  };

  private postMessageToParent(msg: any, origin = '*', ports?: any) {
    this.$window.parent.postMessage(msg, origin);
  }

  private postMessageToFrame(msg: any, origin = '*', ports?: any) {
    const frame = (this.$window.frames as any)['mode-embed-frame'];
    if (frame) {
      frame.postMessage(msg, origin);
    }
  }

  public postMessage(msg: any, origin = '*', ports?: any) {
    if ((this.$window.frames as any)['mode-embed-frame']) {
      this.postMessageToFrame(msg, origin, ports);
    } else {
      this.postMessageToParent(msg, origin, ports);
    }
  }

  /**
   *
   * @param handler The event callback
   * @param type Message type to subscribe to. If omitted, handler will receive all message types.
   * @param context Context for the callback
   * @returns true if handler does not exist and was subscribed, false otherwise
   */
  public subscribe(context: any, handler: (msg: WindowMessagePayload) => void, type = '*') {
    if (this.subscriptions[type] === undefined) {
      this.subscriptions[type] = [];
    }

    const sub = _.find(this.subscriptions[type], { handler, context });
    if (!sub) {
      this.subscriptions[type].push({ handler, context });
      return true;
    }

    return false;
  }

  /**
   * Unsubscribes from post message events. Handler and type are optional. If handler is not provided,
   * then all handlers belonging to context will be unsubscribed. If type is not provided, then all types
   * (all types PLUS '*') will be unsubscribed.
   *
   * @param handler Callback to unsubscribe. If not provided, unsubscribe context from all subs.
   * @param type Message type that was originally subscribed to. If not provided, then unsub '*' and all other types.
   * @param context Context for the callback
   * @returns true if one or more subscriptions have been removed
   */
  public unsubscribe(context: any, handler?: (msg: any) => any, type?: string): boolean {
    let typesToRemove = [type];

    if (!type) {
      typesToRemove = _.keys(this.subscriptions);
    }

    let removed = false;
    typesToRemove.forEach((t) => {
      if (this.subscriptions[t as string]) {
        const sub = _.find(this.subscriptions[t as string], handler ? { handler, context } : { context });
        if (sub) {
          this.subscriptions[t as string].splice(this.subscriptions[t as string].indexOf(sub), 1);
          removed = true;
        }
      }
    });

    return removed;
  }

  public selectTableColumn(tableId: string, column: string) {
    this.postMessage({
      type: MSG_TYPES.SelectTableColumn,
      data: {
        id: tableId,
        column,
      },
    });
  }

  public EnterFullScreen() {
    this.postMessage({
      type: MSG_TYPES.EnterFullScreen,
    });
  }

  public ExitFullScreen() {
    this.postMessage({
      type: MSG_TYPES.ExitFullScreen,
    });
  }

  public updateFeatureState(state: any) {
    this.postMessageToFrame({
      type: MSG_TYPES.UpdateFeatureState,
      state,
    });
  }
}
